import React from 'react';
import { Badge } from './ui/badge';
import { Trophy, Star, Award, Zap, Crown, Sparkles } from 'lucide-react';

export const ReferralMilestoneBadges = ({ referralCount = 0 }) => {
  const milestones = [
    {
      name: 'Starter',
      threshold: 1,
      icon: Sparkles,
      color: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
      description: 'First referral'
    },
    {
      name: 'Influencer',
      threshold: 5,
      icon: Star,
      color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      description: '5 referrals'
    },
    {
      name: 'Ambassador',
      threshold: 10,
      icon: Award,
      color: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      description: '10 referrals'
    },
    {
      name: 'Champion',
      threshold: 25,
      icon: Trophy,
      color: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      description: '25 referrals'
    },
    {
      name: 'Legend',
      threshold: 50,
      icon: Crown,
      color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      description: '50 referrals'
    },
    {
      name: 'Elite',
      threshold: 100,
      icon: Zap,
      color: 'bg-red-500/10 text-red-600 border-red-500/20',
      description: '100 referrals'
    }
  ];

  // Get current milestone
  const currentMilestone = [...milestones]
    .reverse()
    .find(m => referralCount >= m.threshold) || null;

  // Get next milestone
  const nextMilestone = milestones.find(m => referralCount < m.threshold);

  // Get all earned badges
  const earnedBadges = milestones.filter(m => referralCount >= m.threshold);

  return (
    <div className="space-y-4">
      {/* Current Badge */}
      {currentMilestone && (
        <div className="flex items-center justify-center gap-3 p-4 rounded-lg bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
          <div className={`w-12 h-12 rounded-full ${currentMilestone.color} flex items-center justify-center`}>
            <currentMilestone.icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Current Status</p>
            <p className="text-lg font-semibold">{currentMilestone.name}</p>
          </div>
        </div>
      )}

      {/* Next Milestone Progress */}
      {nextMilestone && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">Next: {nextMilestone.name}</p>
            <p className="text-sm text-muted-foreground">
              {referralCount}/{nextMilestone.threshold}
            </p>
          </div>
          <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(referralCount / nextMilestone.threshold) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {nextMilestone.threshold - referralCount} more referrals to unlock
          </p>
        </div>
      )}

      {/* All Badges */}
      <div>
        <p className="text-sm font-medium mb-3">All Milestones</p>
        <div className="grid grid-cols-3 gap-2">
          {milestones.map((milestone) => {
            const isEarned = referralCount >= milestone.threshold;
            const Icon = milestone.icon;

            return (
              <div
                key={milestone.name}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                  isEarned
                    ? milestone.color + ' scale-100'
                    : 'bg-muted/30 text-muted-foreground border-muted opacity-50 grayscale'
                }`}
                title={milestone.description}
              >
                <Icon className="h-5 w-5" />
                <p className="text-xs font-medium text-center">{milestone.name}</p>
                <p className="text-xs text-center">{milestone.threshold}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Achievement Count */}
      {earnedBadges.length > 0 && (
        <div className="text-center pt-2 border-t">
          <Badge variant="secondary" className="text-xs">
            {earnedBadges.length} of {milestones.length} badges earned
          </Badge>
        </div>
      )}
    </div>
  );
};

export default ReferralMilestoneBadges;
